/**
 * Pure splitter math helpers — `clampSplitterWidth` + `readStoredSplitterWidth`.
 *
 * The React hook this file used to export (`useResizableSplitter`) was
 * retired when the Inbox migrated to the canonical `<SplitView>` shell
 * (`src/components/ui/split-view.tsx`). All split-view consumers now
 * use `useResizablePane` (`./use-resizable-pane`), which composes these
 * pure helpers internally — they stay here as the single source of
 * truth for the clamp math, exercised directly by `smoke-inbox-datatable`
 * and indirectly by every operator queue surface.
 */

export type SplitterClampOptions = {
  min: number;
  max: number;
  /** Optional second clamp: a fraction of the container width.
   *  Useful when the container narrows mid-drag (e.g. window
   *  resize) — the panel can't take more than this fraction of the
   *  total width. Defaults to 0.7 (left ≤ 70%). */
  maxFractionOfContainer?: number;
  containerWidth?: number;
};

/**
 * Clamp a candidate splitter width to the configured min/max plus
 * the optional fraction-of-container ceiling. Pure function — same
 * inputs always produce the same output.
 */
export function clampSplitterWidth(
  candidate: number,
  opts: SplitterClampOptions,
): number {
  // NaN is the only "no rational answer" case. ±Infinity falls
  // through to the Math.max/Math.min clamp, where +Infinity ends
  // up at `max` and -Infinity at `min` — semantically correct for
  // a "drag as far as possible" gesture.
  if (Number.isNaN(candidate)) return opts.min;
  let next = Math.max(opts.min, Math.min(opts.max, candidate));
  if (
    opts.containerWidth &&
    Number.isFinite(opts.containerWidth) &&
    opts.containerWidth > 0
  ) {
    const fraction = opts.maxFractionOfContainer ?? 0.7;
    const containerCap = Math.max(opts.min, opts.containerWidth * fraction);
    next = Math.min(next, containerCap);
  }
  return Math.round(next);
}

/**
 * Read a previously-stored width from localStorage. Returns
 * `defaultWidth` when the storage entry is missing, malformed,
 * or out of range — same as the hook's initial state.
 */
export function readStoredSplitterWidth(
  raw: string | null,
  defaultWidth: number,
  opts: { min: number; max: number },
): number {
  if (raw == null) return defaultWidth;
  const n = Number(raw);
  if (!Number.isFinite(n)) return defaultWidth;
  return clampSplitterWidth(n, opts);
}
